import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Mail, Phone, ExternalLink, Loader2, Filter } from "lucide-react";

const STATUS_STYLES = {
  new: "bg-[#FF2B00] text-white border-[#FF2B00]",
  contacted: "bg-zinc-100 text-black border-zinc-300",
  quoted: "bg-[#002FA7] text-white border-[#002FA7]",
  won: "bg-black text-white border-black",
  lost: "bg-white text-zinc-400 border-zinc-300 line-through",
};
const STATUSES = ["new", "contacted", "quoted", "won", "lost"];
const SERVICE_LABEL = {
  fleet_recon: "Fleet Recon", bodyshop: "Body Shop", detail: "Detail",
  paint: "Paint", carwash: "Carwash", porter: "Porter",
};

export default function Estimates() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    const params = filter ? { status: filter } : {};
    const { data } = await api.get("/estimates", { params });
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, [filter]);

  const updateStatus = async (id, status) => {
    await api.patch(`/estimates/${id}`, { status });
    load();
    if (selected?.id === id) setSelected({ ...selected, status });
  };

  const saveNotes = async (id, notes) => {
    await api.patch(`/estimates/${id}`, { admin_notes: notes });
    load();
  };

  return (
    <div className="p-6 lg:p-12" data-testid="estimates-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="label-caps text-[#FF2B00]">Sales Pipeline</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">Estimate Requests</h1>
          <p className="text-zinc-600 text-sm mt-2">Incoming leads from the public site. Click any row to view details.</p>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Filter size={14} className="text-zinc-500" />
        <button onClick={() => setFilter("")} className={`label-caps px-3 py-2 border text-xs ${filter === "" ? "bg-black text-white border-black" : "border-zinc-300"}`} data-testid="est-filter-all">All</button>
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`est-filter-${s}`}
            className={`label-caps px-3 py-2 border text-xs ${filter === s ? "bg-black text-white border-black" : "border-zinc-300 hover:border-black"}`}>
            {s}
          </button>
        ))}
      </div>

      <div className="bg-white border border-zinc-200">
        {loading ? (
          <div className="p-12 text-center text-zinc-500 inline-flex items-center gap-2 justify-center w-full"><Loader2 className="animate-spin" size={14} /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-zinc-500" data-testid="estimates-empty">No estimate requests yet. Share your site link to start collecting leads.</div>
        ) : (
          <>
            <div className="hidden md:grid grid-cols-12 gap-4 p-4 border-b border-zinc-200 label-caps text-zinc-500 text-xs">
              <div className="col-span-2">Service</div>
              <div className="col-span-3">Customer</div>
              <div className="col-span-3">Contact</div>
              <div className="col-span-2">Received</div>
              <div className="col-span-2 text-right">Status</div>
            </div>
            {items.map((e) => (
              <button key={e.id} onClick={() => setSelected(e)} className="w-full text-left grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 p-4 border-b border-zinc-100 hover:bg-zinc-50 items-center" data-testid={`estimate-row-${e.id}`}>
                <div className="col-span-2 font-medium">{SERVICE_LABEL[e.service_type] || e.service_type}</div>
                <div className="col-span-3">
                  <div className="text-sm font-medium">{e.name}</div>
                  {e.company && <div className="text-xs text-zinc-500">{e.company}</div>}
                </div>
                <div className="col-span-3 text-xs text-zinc-600">
                  <div>{e.email}</div>
                  <div>{e.phone}</div>
                </div>
                <div className="col-span-2 text-xs text-zinc-500">{new Date(e.created_at).toLocaleString()}</div>
                <div className="col-span-2 text-right">
                  <span className={`label-caps text-[10px] px-2 py-1 border ${STATUS_STYLES[e.status]}`}>{e.status}</span>
                </div>
              </button>
            ))}
          </>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(ev) => ev.stopPropagation()} data-testid="estimate-detail-modal">
            <div className="p-6 border-b border-zinc-200 flex justify-between items-start">
              <div>
                <div className="label-caps text-[#FF2B00]">{SERVICE_LABEL[selected.service_type] || selected.service_type} Estimate</div>
                <h2 className="font-display text-3xl mt-1">{selected.name}</h2>
                {selected.company && <div className="text-sm text-zinc-600 mt-1">{selected.company}</div>}
              </div>
              <span className={`label-caps text-[10px] px-2 py-1 border ${STATUS_STYLES[selected.status]}`}>{selected.status}</span>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <a href={`tel:${selected.phone}`} className="border border-black p-3 hover:bg-black hover:text-white inline-flex items-center gap-2 label-caps text-xs">
                  <Phone size={14} /> {selected.phone}
                </a>
                <a href={`mailto:${selected.email}?subject=Re: Your ${SERVICE_LABEL[selected.service_type]} estimate`} className="border border-black p-3 hover:bg-black hover:text-white inline-flex items-center gap-2 label-caps text-xs">
                  <Mail size={14} /> {selected.email}
                </a>
              </div>

              <dl className="text-sm space-y-1">
                {selected.vehicle_year || selected.vehicle_make || selected.vehicle_model ? (
                  <div className="flex justify-between border-b border-zinc-100 py-2">
                    <dt className="text-zinc-500">Vehicle</dt>
                    <dd>{[selected.vehicle_year, selected.vehicle_make, selected.vehicle_model].filter(Boolean).join(" ")}</dd>
                  </div>
                ) : null}
                {selected.vehicle_count && (
                  <div className="flex justify-between border-b border-zinc-100 py-2">
                    <dt className="text-zinc-500">Fleet size</dt>
                    <dd>{selected.vehicle_count} vehicles</dd>
                  </div>
                )}
                {selected.damage_description && (
                  <div className="border-b border-zinc-100 py-2">
                    <dt className="text-zinc-500 mb-1">Damage</dt>
                    <dd className="whitespace-pre-wrap">{selected.damage_description}</dd>
                  </div>
                )}
                {selected.insurance_involved != null && (
                  <div className="flex justify-between border-b border-zinc-100 py-2">
                    <dt className="text-zinc-500">Insurance</dt>
                    <dd>{selected.insurance_involved ? "Yes" : "No"}</dd>
                  </div>
                )}
                {selected.notes && (
                  <div className="border-b border-zinc-100 py-2">
                    <dt className="text-zinc-500 mb-1">Notes</dt>
                    <dd className="whitespace-pre-wrap">{selected.notes}</dd>
                  </div>
                )}
                {selected.photos && selected.photos.length > 0 && (
                  <div className="border-b border-zinc-100 py-3">
                    <dt className="text-zinc-500 mb-2 label-caps text-xs">Customer Photos ({selected.photos.length})</dt>
                    <div className="flex flex-wrap gap-3" data-testid="est-detail-photos">
                      {selected.photos.map((p, i) => (
                        <a key={i} href={p} target="_blank" rel="noreferrer" className="block w-32 h-32 border-2 border-black hover:border-[#FF2B00]">
                          <img src={p} alt={`damage ${i}`} className="w-full h-full object-cover" />
                        </a>
                      ))}
                    </div>
                    <p className="text-xs text-zinc-500 mt-2">Click a photo to open full-size in a new tab.</p>
                  </div>
                )}
                <div className="flex justify-between py-2 text-xs text-zinc-500">
                  <dt>Email sent to owner</dt>
                  <dd>{selected.email_sent ? "✓ Delivered" : "✗ Not sent (check EMERGENT_EMAIL_KEY)"}</dd>
                </div>
              </dl>

              <div>
                <label className="label-caps text-zinc-500 block mb-2 text-xs">Update status</label>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((s) => (
                    <button key={s} data-testid={`est-set-${s}`} onClick={() => updateStatus(selected.id, s)}
                      className={`label-caps text-xs px-3 py-2 border ${selected.status === s ? STATUS_STYLES[s] : "border-zinc-300 hover:border-black"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="label-caps text-zinc-500 block mb-2 text-xs">Internal notes</label>
                <textarea
                  data-testid="est-admin-notes"
                  rows={3}
                  defaultValue={selected.admin_notes || ""}
                  onBlur={(e) => saveNotes(selected.id, e.target.value)}
                  className="w-full border border-zinc-300 px-3 py-2 text-sm"
                  placeholder="Follow-up notes — auto-saves on blur"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
